/**
 * The intake queue's upsell rule, kept out of the hook so it can be tested.
 *
 * Types come in through `import type` only: a value import of the entity
 * barrels would pull the Dataverse client and its env config into the test,
 * which is the same reason `renewals/lib/renewalQueue.ts` imports the way it
 * does.
 */

import type { Opportunity } from '@entities/opportunity';
import type { OpsGroup } from '@entities/ops-group';
import type { IntakeRecord } from '@entities/intake-record';

/**
 * A solution sold INTO a group Vera already had (Lita, 9/17, on McClancy: an
 * existing Rx group that bought Anchor, "it's a newly sold product, it should
 * flow through implementation").
 *
 * Before this, every won deal whose group already existed read as `enrolled`
 * and IntakePage dropped enrolled rows outright, so an upsell was structurally
 * invisible: the opportunity arrived and the queue swallowed it. Sasha: "if
 * it's there, it should show up. Of course it should just show up."
 *
 * Two tests, both cheap, so this adds no query to a page that is already a
 * heavy join:
 *
 *   NO INTAKE RECORD. Enrolling a new group leaves its deal at `in-review`,
 *   and Link/Dismiss leave `linked`/`dismissed`, so a record means somebody has
 *   worked this deal. Only an untouched one can be an unnoticed upsell.
 *
 *   CLOSED AFTER THE GROUP EXISTED. Without this, every pre-intake deal whose
 *   group was created from that very deal would flood the queue. A missing date
 *   on either side reads as "not an upsell", which keeps today's behaviour
 *   rather than guessing.
 *
 * Deliberately NOT a solutions comparison. `matchProducts` could say exactly
 * which sold products the group lacks, but it needs the catalog solution list,
 * the Solution Categories config and a book-wide group-solution read, and the
 * question here is only "has anyone looked at this deal", which these two
 * answer. The drawer still shows what was sold when someone opens the row.
 */
export function soldIntoExistingGroup(
  opp: Pick<Opportunity, 'actualCloseDate'>,
  existingGroup: (Pick<OpsGroup, 'createdOn'> & Partial<Pick<OpsGroup, 'provisional'>>) | undefined,
  intakeRecord: Pick<IntakeRecord, 'status'> | undefined,
): boolean {
  // A PRE-ENROLLED group is not "a group Vera already had": it was created off
  // this very deal so the admin fee breakout could start, so a sale into it is
  // the original sale, not an upsell (docs/design/pre-enrollment.md).
  if (existingGroup?.provisional) return false;
  if (!existingGroup || intakeRecord) return false;
  const closed = opp.actualCloseDate?.split('T')[0];
  const created = existingGroup.createdOn?.split('T')[0];
  if (!closed || !created) return false;
  return closed > created;
}
